import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown, FaRegCalendarAlt } from "react-icons/fa";
import { usePortalPosition } from "../../TaskManagement/usePortalPosition";
import {
  DATE_PRESETS,
  FILTER_CHEVRON_CLASS,
  FILTER_LABEL_CLASS,
  FILTER_TRIGGER_CLASS,
  FILTER_TRIGGER_OPEN_CLASS,
  formatDisplayDate,
  getDropdownOptionStyle,
  resolveDateRange,
} from "./commissionsConstants";

function getDisplayLabel(value) {
  const preset = value?.preset || "all";
  const presetLabel = DATE_PRESETS.find((p) => p.id === preset)?.label || "All time";

  if (preset === "custom" && value?.startDate && value?.endDate) {
    return `${formatDisplayDate(value.startDate)} – ${formatDisplayDate(value.endDate)}`;
  }

  if (preset === "last_7_days" || preset === "last_30_days") {
    const { startDate, endDate } = resolveDateRange(value);
    if (startDate && endDate) {
      return `${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`;
    }
  }

  return presetLabel;
}

function CommissionsDateRangeField({ label = "Date Range", value, onChange }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const portalStyle = usePortalPosition(triggerRef, open, { placement: "bottom", gap: 6 });

  const preset = value?.preset || "all";
  const displayLabel = getDisplayLabel(value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inContainer = containerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inContainer && !inMenu) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePresetChange = (id) => {
    if (id === "custom") {
      onChange({ preset: "custom", startDate: value?.startDate || "", endDate: value?.endDate || "" });
    } else {
      onChange({ preset: id, startDate: "", endDate: "" });
      if (id !== "custom") setOpen(false);
    }
  };

  const menu = (
    <div
      ref={menuRef}
      className="tailwind-container z-[9999] rounded-lg border border-gray-200 bg-white p-2.5 shadow-lg"
      style={portalStyle}
    >
      <div className="space-y-0.5">
        {DATE_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => handlePresetChange(p.id)}
            className="text-[13px] transition"
            style={{
              ...getDropdownOptionStyle(preset === p.id),
              borderRadius: "6px",
              padding: "6px 10px",
            }}
            onMouseEnter={(e) => {
              if (preset !== p.id) e.currentTarget.style.backgroundColor = "#F9FAFB";
            }}
            onMouseLeave={(e) => {
              if (preset !== p.id) e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      {preset === "custom" && (
        <div className="mt-2 space-y-2 border-t border-gray-100 pt-2">
          <div>
            <label className="mb-1 block text-[11px] font-medium text-gray-500">Start</label>
            <input
              type="date"
              value={value?.startDate || ""}
              onChange={(e) =>
                onChange({ ...value, preset: "custom", startDate: e.target.value })
              }
              className="h-[34px] w-full rounded-lg border border-gray-200 px-2 text-[13px] outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-medium text-gray-500">End</label>
            <input
              type="date"
              value={value?.endDate || ""}
              min={value?.startDate || undefined}
              onChange={(e) =>
                onChange({ ...value, preset: "custom", endDate: e.target.value })
              }
              className="h-[34px] w-full rounded-lg border border-gray-200 px-2 text-[13px] outline-none focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20"
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-w-0 flex-1 basis-0" ref={containerRef}>
      <label className={FILTER_LABEL_CLASS}>{label}</label>
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((p) => !p)}
          className={`${FILTER_TRIGGER_CLASS} ${open ? FILTER_TRIGGER_OPEN_CLASS : ""} cursor-pointer`}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 truncate text-left">
            <FaRegCalendarAlt size={12} className="shrink-0 text-gray-400" />
            <span className={`truncate ${displayLabel === "All time" ? "text-gray-400" : "text-gray-800"}`}>
              {displayLabel}
            </span>
          </span>
          <FaChevronDown
            size={10}
            className={`${FILTER_CHEVRON_CLASS} ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && createPortal(menu, document.body)}
      </div>
    </div>
  );
}

export default CommissionsDateRangeField;
